import { useEffect, useState } from 'react';
import { census2022Api, type SliceRow2022 } from './census-2022-api';

interface Census2022SlicesPreviewProps {
  projectId: string;
}

function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return Math.round(n).toLocaleString('es-AR');
}

function pct(n: number | null | undefined, decimals = 2): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(decimals)}%`;
}

export function Census2022SlicesPreview({ projectId }: Census2022SlicesPreviewProps): JSX.Element {
  const [partialSlices, setPartialSlices] = useState<SliceRow2022[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    setError(null);

    // Fetch enough slices to find the partials (coverage_ratio < 0.999)
    // Backend does not have a filter param, so we fetch first 200 and filter client-side
    census2022Api.getSlices(projectId, { limit: 200 })
      .then((res) => {
        const partials = res.slices
          .filter((s) => !s.isFull)
          .sort((a, b) => a.coverageRatio - b.coverageRatio)
          .slice(0, 10);
        setPartialSlices(partials);
      })
      .catch(() => setError('Error al cargar radios'))
      .finally(() => setLoading(false));
  }, [projectId]);

  if (loading) {
    return <div className="c22-slices-loading">Cargando radios parciales…</div>;
  }

  if (error) {
    return <div className="c22-slices-error">{error}</div>;
  }

  if (partialSlices.length === 0) {
    return (
      <div className="c22-slices-empty">
        Sin radios parciales — todos completos dentro del área del proyecto.
      </div>
    );
  }

  return (
    <div className="c22-slices-preview">
      <div className="c22-slices-title">
        Radios parciales ({partialSlices.length > 9 ? 'top 10' : partialSlices.length})
      </div>
      <div className="c22-slices-table-wrap">
        <table className="c22-slices-table">
          <thead>
            <tr>
              <th>Radio</th>
              <th>Depto.</th>
              <th title="Porcentaje del radio dentro del área del proyecto">% dentro</th>
              <th>Pob. est.</th>
              <th>Hog. est.</th>
            </tr>
          </thead>
          <tbody>
            {partialSlices.map((s) => (
              <tr key={s.id} className={s.coverageRatio < 0.1 ? 'c22-row--sliver' : ''}>
                <td className="c22-cell--code">{s.radioCode}</td>
                <td className="c22-cell--dept" title={s.departmentName ?? undefined}>
                  {s.departmentName ?? '—'}
                </td>
                <td className="c22-cell--pct">{pct(s.coverageRatio)}</td>
                <td className="c22-cell--num">{fmt(s.popTotalWeighted)}</td>
                <td className="c22-cell--num">{fmt(s.householdsTotalWeighted)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
